import { appState } from '../../../../interfaces/common';
import { Flex, Grid, Input, Text } from '@pega/cosmos-react-core';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { AgenticLoaderPreview } from '../../../../interfaces/chat';
import { setChatInfo, updateBannerInfo, updateLoaderDetails } from '../../../../redux/actions';
import { updateUIToggle } from '../ChatApis';
import UploadLogo from '../Modals/UploadLogo';
import ChatContainer from './ChatPreview/ChatContainer';
import DisplayToggles from './DisplayToggles';
import ContrastWarning from '../../../Utilities/ContrastWarning';
import useContrastChecker from '../../../../hooks/useContrastChecker';

export const colorPickerStyle = {
  maxWidth: '2rem',
  minHeight: '2rem',
  height: '10px',
  padding: 0,
};

const StylingTab: React.FC = () => {
  const {
    colorTheme = '#0000ff',
    connectionId,
    headerTextColor = '#ffffff',
    hideChatLauncher,
  } = useSelector((state: appState) => state.chatInfo);
  const contrastChecker = useContrastChecker();
  const { sessionId } = useSelector((state: appState) => {
    return {
      accounts: state.accounts,
      sessionId: state.sessionId,
    };
  });
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const navigate = useNavigate();
  const [selectedColors, setSelectedColors] = useState({
    selectedColorTheme: '',
    selectedHeaderTextColor: '',
  });
  const [contrastWarning, setContrastWarning] = useState('');
  const [contrastWarningForWidgetTheme, setContrastWarningForWidgetTheme] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMultiExpExpanded, setIsMultiExpExpanded] = useState(false);
  const [agenticPreview, setAgenticPreview] = useState<AgenticLoaderPreview | null>(null);

  useEffect(() => {
    setSelectedColors(() => {
      return {
        selectedColorTheme: colorTheme,
        selectedHeaderTextColor: headerTextColor,
      };
    });
    setContrastWarning(contrastChecker(colorTheme, headerTextColor));
    /** Here assuming white background for theme color as the icons are shown on white background */
    setContrastWarningForWidgetTheme(contrastChecker('#ffffff', colorTheme));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorTheme, headerTextColor]);

  useEffect(() => {
    setAgenticPreview(null);
  }, [connectionId]);

  useEffect(() => {
    if (hideChatLauncher && !isExpanded) {
      setIsExpanded(true);
    }
  }, [hideChatLauncher, setIsExpanded, isExpanded]);

  const updateToggleConfig = async (togglePath: string, payload: any, bannerParam: string) => {
    dispatch(updateLoaderDetails(true));
    const json = await updateUIToggle(togglePath, sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate(`${bannerParam}_update_success`), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate(`${bannerParam}_update_failed`), 'urgent'));
    }
  };

  const handleColorChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    await updateToggleConfig(
      'update_font_bg_color',
      {
        colorTheme: value,
        type: 'Color_Theme',
      },
      'color_theme',
    );
  };

  const handleHeaderColorChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setContrastWarning(contrastChecker(colorTheme, headerTextColor));
    await updateToggleConfig(
      'update_font_bg_color',
      {
        headerTextColor: value,
        type: 'Header_Text_Color',
      },
      'head_text_color',
    );
  };

  const handleAgenticPreviewChange = useCallback((enabled: boolean, phrases: string[]) => {
    setAgenticPreview({ enabled, phrases });
  }, []);

  return (
    <Grid
      container={{
        cols: 'repeat(2, 55% 45% )',
      }}
    >
      <Flex
        container={{
          direction: 'column',
          itemGap: 3.5,
        }}
      >
        <Flex container={{ direction: 'column', itemGap: 3 }} item={{ grow: 1 }}>
          <Flex container={{ direction: 'column', itemGap: 2 }}>
            <Text variant="h3" as="h3">
              {translate('widget')}
            </Text>

            <Grid
              container={{
                cols: 'repeat(2, 1fr)',
                gap: 2,
              }}
            >
              <Flex
                container={{
                  direction: 'row',
                  itemGap: 1,
                  alignItems: 'end',
                }}
              >
                <Flex item={{ grow: 1 }}>
                  <Input
                    label={translate('primary_color')}
                    type="text"
                    readOnly={true}
                    status={contrastWarningForWidgetTheme ? 'warning' : undefined}
                    name="colorTheme"
                    value={colorTheme || '#0000ff'}
                  />
                </Flex>
                <Input
                  autoComplete="off"
                  type="color"
                  style={colorPickerStyle}
                  value={colorTheme || '#0000ff'}
                  onChange={handleColorChange}
                />
              </Flex>
            </Grid>
            {contrastWarningForWidgetTheme && <ContrastWarning warningMessage={contrastWarningForWidgetTheme} />}
            <Grid
              container={{
                cols: 'repeat(2, 1fr)',
                gap: 2,
              }}
            >
              <Flex
                container={{
                  direction: 'row',
                  itemGap: 1,
                  alignItems: 'end',
                }}
              >
                <Flex item={{ grow: 1 }}>
                  <Input
                    label={translate('header_text_color')}
                    type="text"
                    readOnly={true}
                    status={contrastWarning ? 'warning' : undefined}
                    name="headerTextColor"
                    value={headerTextColor || '#ffffff'}
                  />
                </Flex>
                <Input
                  autoComplete="off"
                  type="color"
                  style={colorPickerStyle}
                  value={headerTextColor || '#ffffff'}
                  onChange={handleHeaderColorChange}
                />
              </Flex>
            </Grid>
            {contrastWarning && <ContrastWarning warningMessage={contrastWarning} />}
          </Flex>
        </Flex>
        {connectionId && <UploadLogo isMultiExpExpanded={isMultiExpExpanded} />}
        {connectionId && <DisplayToggles onAgenticPreviewChange={handleAgenticPreviewChange} />}
      </Flex>
      <Flex>
        <ChatContainer
          selectedColors={selectedColors}
          isMultiExpExpanded={isMultiExpExpanded}
          setIsMultiExpExpanded={setIsMultiExpExpanded}
          agenticPreview={agenticPreview}
        />
      </Flex>
    </Grid>
  );
};

export default StylingTab;
